import { useState, useRef } from 'react'
import { Upload, X, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { getFirebaseStorage, getFirebaseDb } from '@/lib/firebase'
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage'
import { doc, updateDoc } from 'firebase/firestore'
import { cn } from '@/lib/utils'

interface OrganizationLogoUploadProps {
  organizationId: string
  currentLogoUrl?: string | null
  onLogoChange?: (url: string | null) => void
  className?: string
}

export function OrganizationLogoUpload({
  organizationId,
  currentLogoUrl,
  onLogoChange,
  className
}: OrganizationLogoUploadProps) {
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [logoUrl, setLogoUrl] = useState<string | null>(currentLogoUrl || null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file')
      return
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      setError('Image must be less than 2MB')
      return
    }

    setError(null)
    setUploading(true)

    try {
      const storage = getFirebaseStorage()
      const db = getFirebaseDb()
      
      // Create storage reference
      const storageRef = ref(storage, `organizations/${organizationId}/logo`)
      
      // Upload file
      await uploadBytes(storageRef, file)
      
      // Get download URL
      const downloadUrl = await getDownloadURL(storageRef)
      
      // Update organization document with logo URL
      const orgRef = doc(db, 'organizations', organizationId)
      await updateDoc(orgRef, { logoUrl: downloadUrl })
      
      setLogoUrl(downloadUrl)
      onLogoChange?.(downloadUrl)
    } catch (err) {
      console.error('Error uploading logo:', err)
      setError('Failed to upload logo. Please try again.')
    } finally {
      setUploading(false)
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }

  const handleRemoveLogo = async () => {
    setUploading(true)
    setError(null)

    try {
      const storage = getFirebaseStorage()
      const db = getFirebaseDb()
      
      // Delete from storage
      const storageRef = ref(storage, `organizations/${organizationId}/logo`)
      try {
        await deleteObject(storageRef)
      } catch (e) {
        // Ignore if file doesn't exist
      }
      
      // Update organization document
      const orgRef = doc(db, 'organizations', organizationId)
      await updateDoc(orgRef, { logoUrl: null })
      
      setLogoUrl(null)
      onLogoChange?.(null)
    } catch (err) {
      console.error('Error removing logo:', err)
      setError('Failed to remove logo. Please try again.')
    } finally {
      setUploading(false)
    }
  }

  return (
    <div className={cn("space-y-4", className)}>
      <div className="flex items-center gap-4">
        {/* Logo preview */}
        <div className="relative w-20 h-20 rounded-lg border-2 border-dashed border-muted-foreground/25 flex items-center justify-center overflow-hidden bg-muted">
          {logoUrl ? (
            <>
              <img 
                src={logoUrl} 
                alt="Organization logo" 
                className="w-full h-full object-cover"
              />
              {!uploading && (
                <button
                  onClick={handleRemoveLogo}
                  className="absolute top-1 right-1 p-1 bg-destructive text-destructive-foreground rounded-full hover:bg-destructive/90 transition-colors"
                  title="Remove logo"
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </>
          ) : (
            <Upload className="w-8 h-8 text-muted-foreground/50" />
          )}
          {uploading && (
            <div className="absolute inset-0 bg-background/80 flex items-center justify-center">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          )}
        </div>

        {/* Upload button */}
        <div className="flex-1">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            className="hidden"
            disabled={uploading}
          />
          <Button
            type="button"
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
          >
            {uploading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Uploading...
              </>
            ) : logoUrl ? (
              'Change Logo'
            ) : (
              'Upload Logo'
            )}
          </Button>
          <p className="mt-1 text-xs text-muted-foreground">
            PNG, JPG, GIF up to 2MB
          </p>
        </div>
      </div>

      {error && (
        <p className="text-sm text-destructive">{error}</p>
      )}
    </div>
  )
}
